import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Badge from "../../components/badges/Badges";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { Messages, Token, SearchMessage, RdbMessageListTimestampToDate, MessageSearchDefault, MessageView, defaultMessageSearchData } from "../../model/Message";
import { MessageEditModal } from "./MessageEditModal";
import messagesQuery from '../../firestore/Messages.query';
import { Spiner } from "../../components/spinner/Spiner";
import Button from "../../components/button/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { convertDateTimeToString } from "../../utility/ConvertUtily";
import { Company } from "../../model/Companies";
import { auth } from "../../firebase";
import { Groups } from "../../model/Groups";
import { User } from "../../model/Users";
import { ReplyButton } from "../../model/ReplyButton";
import { OnlineUrls } from "../../model/OnlineUrls";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { MessagePreviewModal } from "./MessagePreviewModal";
import { GroupSelectModal } from "./GroupSelectModal";
import { UserSelectModal } from "./UserSelectModal";
import { MessagingIntController } from "../../controller/MessagingInitController";
import { GroupSelect } from "../../controller/GroupSelectController";
import { UserSelect } from "../../controller/UserSelectController";
import { MessagingCommonFunctions } from "../../controller/MessagingCommonFunctionsController";
import { requestRdbMessages } from "../../api/Messages.api";
import { ConstMessage } from "../../config/const";
import { createEvenReplyButtons } from "../../utility/CreateEventReplyButtonUtility";
import { MessageSearch } from "./MessageSearch";
import { CheckBox } from "../../components/checkBox/CheckBox";
import { MessageDeleteModal } from "./MessageDeleteModal";
import { Progress } from "../../components/progress/Progress";
import { QuestionnaireForm } from "./QuestionnaireModal";
import { requestGetTargetUser } from "../../api/Users.api";

export const MessageList = () => {
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const location = useLocation();
  const isDisabled = useRef(false);

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const messageInit = new MessagingIntController();
  const messageCommonFunctions = new MessagingCommonFunctions();
  const groupSelect = new GroupSelect();
  const userSelect = new UserSelect();

  const delayLoadFlg = useRef<boolean>(location.state === null ? false : true);
  const userListLoadFlg = useRef<boolean>(false);
  const requestIdRef = useRef<number>(0);
  const prevCreateMessageFlgRef = useRef<boolean>(false);
  const prevSearchConditionForPageResetRef = useRef<any>(null);

  // MessageList・MessageInfo・Schedule共通
  const [initFlg, setInitFlg] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [isOpenPreview, setIsOpenPreview] = useState<boolean>(false);
  const [company, setCompany] = useState<Company>();
  const [groupList, setGroupList] = useState<Groups[]>([]);
  const [filteredGroupList, setFilteredGroupList] = useState<Groups[]>([]);
  const [userList, setUserList] = useState<User[]>([]);
  const [filteredUserList, setFilteredUserList] = useState<User[]>(userList);
  const [replyButtonList, setReplyButtonList] = useState<ReplyButton[]>([]);
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireForm | undefined>(undefined);
  const [replyButtonSelected, setReplyButtonSelected] = useState<ReplyButton | undefined>();
  const [defaultReplyButtonList, setDefaultReplyButtonList] = useState<ReplyButton[]>([]);
  const [eventReplyButtonList, setEventReplyButtonList] = useState<ReplyButton[]>([]);
  const [onlineUrlList, setOnlineUrlList] = useState<OnlineUrls[]>([]);
  const [onlineUrlSelected, setOnlineUrlSelected] = useState<string>();
  const [topicState, setTopicState] = useState<string[]>([]);
  const [tokenState, setTokenState] = useState<string[]>([]);
  const [token, setToken] = useState<Token[]>([]);
  const [isGroupSelectOpen, setIsGroupSelectOpen] = useState(false);
  const [isUserSelectOpen, setIsUserSelectOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [base64Image, setBase64Image] = useState<string>('');
  const [previewData, setPreviewData] = useState<Messages>();
  const [groupFormRowList, setGroupFormRowList] = useState<GridRow[]>([]);
  const [groupCheckedIdList, setGroupCheckedIdList] = useState<string[]>(topicState);
  const [userFormRowList, setUserFormRowList] = useState<GridRow[]>([]);
  const [userCheckedIdList, setUserCheckedIdList] = useState<string[]>(tokenState);
  const [selectedRowList, setSelectedRowList] = useState<GridRow[]>([]);

  const [messageList, setMessageList] = useState<MessageView[]>([]);
  const [addMessage, setAddMessage] = useState<MessageView | null>(null);
  const [deleteMessageIds, setDeleteMessageIds] = useState<string[]>([]);

  const [userCount, setUserCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [selectNumber, setSelectNumber] = useState(50);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [changedCurrentPage, setChangedCurrentPage] = useState<number>(0);
  const [checkedAllValue, setCheckedAllValue] = useState<boolean>(false);
  const [deleteCheckFlag, setDeleteCheckFlag] = useState<boolean>(false);
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [firstPageFlg, setFirstPageFlg] = useState(false);
  const [createMessageFlg, setCreateMessageFlg] = useState<boolean>(false);
  const [selectPage, setSelectPage] = useState<number>(1);
  const [checkedValue, setCheckedValue] = useState("");
  const [checkedDate, setCheckedDate] = useState("");
  const [searchMessageItem, setSearchMessageItem] = useState<SearchMessage>(MessageSearchDefault);
  const [searchCondition, setSearchCondition] = useState<any>(defaultMessageSearchData());
  const [gridRowLength, setGridRowLength] = useState<number>(0);

  // 初期データ取得 MessageList・MessageInfo・Scheduleほぼ共通
  useMemo(() => {
    const f = async () => {
      try {
        await messageInit.main(
          currentUser?.tenantId!,
          '',
          setCompany,
          setGroupList,
          setFilteredGroupList,
          setReplyButtonList,
          setDefaultReplyButtonList,
          undefined,
          undefined,
          setEventReplyButtonList,
          setOnlineUrlList
        );
      } catch (err :any) {
        dispatch(alertOpen(AlertValue("失敗しました", "danger")));
      };
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // お知らせ一覧を取得する共通処理
  useEffect(() => {
    const f = async () => {
      if (company) {
        let effectiveSelectPage = selectPage;
        let needsPageReset = false;

        // 検索条件が変わった時の処理
        if (prevSearchConditionForPageResetRef.current !== null && 
            JSON.stringify(prevSearchConditionForPageResetRef.current) !== JSON.stringify(searchCondition)) {
          prevSearchConditionForPageResetRef.current = searchCondition;
          effectiveSelectPage = 1;
          needsPageReset = selectPage !== 1;
        }
        if (prevSearchConditionForPageResetRef.current === null) {
          prevSearchConditionForPageResetRef.current = searchCondition;
        }

        // 新規作成時の処理
        if (prevCreateMessageFlgRef.current !== createMessageFlg && addMessage) {
          prevCreateMessageFlgRef.current = createMessageFlg;
          effectiveSelectPage = 1;
          needsPageReset = selectPage !== 1;
        }

        // ページリセットが必要な場合、状態を更新して再度useEffectを発火
        if (needsPageReset) {
          setSelectPage(1);
          setFirstPageFlg(true);
          return;
        }

        // ページ1の場合でもfirstPageFlgを更新
        if (effectiveSelectPage === 1 && selectPage === 1) {
          setFirstPageFlg(true);
        }

        // 投稿数上限
        setUserCount(company.usersCount * 4);

        // リクエストIDをインクリメント
        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;
        
        await getMessages(currentRequestId, effectiveSelectPage);
        // 最新のリクエストの場合のみローディングを解除
        if (currentRequestId === requestIdRef.current) {
          setIsLoad(false);
        }
      }
    }
    f();
  }, [groupList, selectPage, selectNumber, searchCondition, deleteCheckFlag, createMessageFlg]);

  // お知らせ一覧取得
  async function getMessages(requestId: number, pageOverride?: number) {
    // 最新のリクエストの場合のみ状態を更新
    if (requestId === requestIdRef.current) {
      const effectivePage = pageOverride ?? selectPage;
      const result = await requestRdbMessages(
        currentUser!.tenantId ?? "",
        company?.displayId ?? "",
        currentUser!.uid,
        (selectNumber + 1).toString(),
        effectivePage.toString(),
        searchCondition,
        deleteMessageIds
      );
      if (requestId !== requestIdRef.current) return;
      if (result.success) {
        const resultMessageList = result.data.data.map((msg: any) => RdbMessageListTimestampToDate(msg));// 今月の投稿数
        const count = await messagesQuery.getMonthlyMessageCount(currentUser?.tenantId!);
        if (requestId !== requestIdRef.current) return; 
        setMessageCount(count);

        // 新規作成データのフィルタリング：RDBの範囲内 & 検索条件に合致するもののみ
        let mergedMessageList: MessageView[] = resultMessageList;

        if (resultMessageList.length > 0 && addMessage) {
          // RDBデータの範囲を取得（最後のレコード）
          const lastRecord = resultMessageList[resultMessageList.length - 1];

          const lastSendDate = new Date(lastRecord.sendDate).getTime();
          const lastCreateDate = new Date(lastRecord.createAt).getTime();

          // 新規作成データが範囲内かつ検索条件に合致するかチェック
          const msgSendDate = new Date(addMessage.sendDate).getTime();
          const msgCreateDate = new Date(addMessage.createAt).getTime();

          // 範囲チェック：最後のレコードより未来（送信日時が後、または送信日時が同じで作成日時が後）
          const isInRange = 
            msgSendDate > lastSendDate || (msgSendDate === lastSendDate && msgCreateDate > lastCreateDate);

          // 既にリストに存在するかチェック
          const existingIndex = mergedMessageList.findIndex(msg => msg.id === addMessage.id);
          if (existingIndex >= 0) {
            // 既にある場合は差し替え
            mergedMessageList[existingIndex] = addMessage;
          } else if (isInRange) {
            // ない場合で範囲内なら追加
            mergedMessageList.push(addMessage);
            // if (addMessage.publishState === "配信済み") monthlyCount++;

            // RDBと同じソート順序（送信日時 DESC, 作成日時 DESC）
            mergedMessageList.sort((a, b) => {
              const sendDateA = new Date(a.sendDate).getTime();
              const sendDateB = new Date(b.sendDate).getTime();
              // 送信日時で比較
              if (sendDateB !== sendDateA) return sendDateB - sendDateA;

              // 送信日時が同じ場合は作成日時で比較
              return new Date(b.createAt).getTime() - new Date(a.createAt).getTime();
            });
          }
          setAddMessage(null);
          setDeleteMessageIds([]);
        }

        // 表示件数分だけ取得
        mergedMessageList = mergedMessageList.slice(0, selectNumber);

        setMessageList(mergedMessageList);
        setGridRowLength(result.data.total);
      } else {
        console.log(result.data)
        dispatch(alertOpen(AlertValue("お知らせ一覧の取得に失敗しました。", "danger")));
      }
    }
  }

  // ページング処理
  const paging = useCallback((page: number) => {
      setFirstPageFlg(false);
      setSelectPage(page);
    }, []);

  // 検索ボタン押下時の処理
  const handleSearch = (condition: any) => {
    setSelectPage(1);
    setFirstPageFlg(true);
    setSearchCondition(condition);
  };

  // 検索時と表示件数変更時に1ページ目を表示する
  useEffect(() => {
    setFirstPageFlg(true)
  }, [selectNumber, gridRowLength])

  // ローディング表示完了用のプロパティ
  useEffect(() => {
    if(loadProgress >= 100){
      setLoadProgress(-1);
    }
  }, [loadProgress]);

  //削除チェックボックス(全選択)
  const checksAll = () => {
    const targetList: (string)[] = [];
    setCheckedList([]);
    if (checkedAllValue === false) {
      //選択対象（検索結果表示またはお知らせ一覧全件表示）
      for (let i = 0; i < messageList.length; i++) {
        let data = messageList[i];
        //チェックボックスがグレーアウトの場合
        if((data.deliveredFlg === true) || (data.draftFlg === false && data.publishEndAt !== null && data.publishEndAt <= new Date())){
          //何もしない
        } else {
          targetList.push(data.id);
        }
        setCheckedList(targetList);
      }
      setCheckedAllValue(true);
    } else {
      setCheckedAllValue(false);
    }
  }

  //削除チェックボックス(個別)
  const checkedListAdd = (messageId: any) => {
    console.log(checkedList)
    console.log(messageId);
    if (checkedList.includes(messageId)) {
      // 存在する場合は削除
      setCheckedList([...checkedList.filter((item) => item != messageId)]);
      setCheckedAllValue(false);
    } else {
      // 存在しない場合は追加
      setCheckedList([...checkedList, messageId]);
    }
  }

  //ページ移動時のチェックボックス表示変更
  useEffect(() => {
    setCheckedAllValue(false);
    setCheckedList([]);
  }, [changedCurrentPage]);

  // お知らせ一覧カラム
  const columns: GridCol[] = [
    {
      field: "checkBox", headerName:
        <CheckBox
          id={"checksAll"}
          onChange={checksAll}
          checked={checkedAllValue}
        />, style: "w-1/12", sort: false
    },
    { field: "title", headerName: "タイトル", style: "w-2/12", sort: false },
    { field: "publishState", headerName: "ステータス", style: "w-1/12", sort: false },
    { field: "topic", headerName: "配信先", style: "w-1/12", sort: false },
    { field: "sendDate", headerName: "配信日時", style: "w-1/12", sort: false },
    { field: "replyButton", headerName: "返信ボタン", style: "w-1/12", sort: false },
    { field: "eventAt", headerName: "イベント開催日", style: "w-1/12", sort: false },
    { field: "authorName", headerName: "作成者", style: "w-1/12", sort: false },
    { field: "createAt", headerName: "作成日時", style: "w-1/12", sort: false },
    { field: "menu", headerName: "", style: "w-1/12", sort: false }
  ];

  // お知らせ一覧データ
  const rowList: GridRow[] = useMemo(() => {
    return messageList.map<GridRow>((item: MessageView) => {
      const title = item.title === "" || item.title === null
        ? (<span className={"text-gray-300"}>タイトルなし</span>)
        : item.title;
      const authorName = item.authorName ?? '';
      // 配信状態の切り替え
      let publishStateText = item.publishState;
      let publishStateBadgeColor: 'red' | 'green' | 'gray' | 'blue' | '' = item.draftFlg
        ? 'blue'
        : item.deliveredFlg
          ? 'green'
          : item.publishEndAt !== null && item.publishEndAt <= new Date()
            ? 'gray'
            : 'red';

    const replyButton = item.replyButtonId !== null
      ? <Badge color="purple" className="ml-2 pr-1 pl-1"><ClipboardDocumentListIcon className="h-4 w-4" /></Badge>
      : '';

    let publishTargetText = item.publishTarget;
    let publishTargetBadgeColor: 'green' | 'indigo' | 'yellow' = item.select === company?.displayId! || item.select ==='all'
      ? 'green'
      : item.select === 'topic'
        ? 'indigo'
        : 'yellow';

      return {
        key: item.id,
        rowData: {
          publishState: <Badge color={publishStateBadgeColor}>{publishStateText}</Badge>,
          checkBox:
            item.deliveredFlg || (item.draftFlg === false && item.publishEndAt !== null && item.publishEndAt <= new Date()) ?
              <CheckBox
                id={"disabledCheck"}
                disabled={true}
                inputClassName={"bg-gray-300"}
              /> :
              <CheckBox
                id={item.id}
                class={"checks"}
                onChange={() => checkedListAdd(item.id)}
                checked={checkedList.includes(item.id)}
              />
          ,
          title: title,
          authorName: authorName,
          replyButton: replyButton,
          createAt: convertDateTimeToString(item.createAt),
          sendDate: convertDateTimeToString(item.sendDate),
          eventAt: item.eventFlg ? convertDateTimeToString(item.eventAt!) :  "",
          topic: <div className="flex">{publishTargetText !== '' ? <Badge color={publishTargetBadgeColor}>{publishTargetText}</Badge> : <></>}</div>,
          menu: (
            <Button
              onClick={() => navigation(`${item.id}/info`)}
              className={"sm:px-3"}
              children="詳細" />
          )
        },
        sortableRowData: {
          publishState: publishStateText,
          title: item.title ?? 'なし',
          topic: publishTargetText,
        },
        style: "truncate max-w-0 sm:whitespace-normal",
      };
    });
  }, [messageList, checkedList, gridRowLength, firstPageFlg])

  // グループ選択後の表のカラム MessageList・MessageInfo・Schedule共通
  const selectedColumns = groupSelect.selectedColumns;

  useEffect(() => {
    // グループ選択後の表のデータ MessageList・MessageInfo・Schedule共通
    const selectedRowList = groupSelect.selectedRowList(groupList, topicState);

    // グループ選択時に個別で選択された場合に個別を表に追加するデータ
    tokenState.forEach((userId) => {
      const userData = userList?.filter((user) => user.id === userId)[0];
      selectedRowList.push({
        rowData: {
          name: (<div className="whitespace-normal">{userData?.lastName}</div>),
          explanation: (<div className="whitespace-normal">個別配信</div>),
        }
      });
    });
    setSelectedRowList(selectedRowList);
    setGroupCheckedIdList(topicState);
    setUserCheckedIdList(tokenState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupList, topicState, tokenState, userList]);

  // 配信先グループ選択モーダルカラム MessageList・MessageInfo・Schedule共通
  const groupFormColumns = groupSelect.groupFormColumns;

  // 配信先利用者選択モーダルカラム MessageList・MessageInfo・Schedule共通
  const userFormColumns = userSelect.userFormColumns;

  // グループ選択 MessageList・MessageInfo・Schedule共通
  const onSelectTopic = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    groupSelect.groupCheckedId(e, id, groupCheckedIdList, setGroupCheckedIdList);
  };
  // 個別配信選択 MessageList・MessageInfo・Schedule共通
  const onSelectToken = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    userSelect.userCheckedId(e, id, userCheckedIdList, setUserCheckedIdList);
  };

  // 配信先グループ選択モーダルデータ MessageList・MessageInfo・Schedule共通
  useEffect(() => {
    const groupFormRowList = groupSelect.groupFormRowList(filteredGroupList, onSelectTopic, groupCheckedIdList);
    setGroupFormRowList(groupFormRowList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGroupList, topicState, groupCheckedIdList]);

  // 配信先利用者選択モーダルデータ MessageList・MessageInfo・Schedule共通
  useEffect(() => {
    const userListRow = userSelect.userListRow(filteredUserList, onSelectToken, userCheckedIdList);
    setUserFormRowList(userListRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUserList, tokenState, userCheckedIdList]);

  // 配信先グループ検索 MessageList・MessageInfo・Schedule共通
  const onSearch = (e: any) => groupSelect.search(e, groupList, setFilteredGroupList);

  // 配信先利用者検索 MessageList・MessageInfo・Schedule共通
  const onUserSearch = (e: any) => userSelect.search(e, userList, setFilteredUserList);

  // 配信先グループ選択モーダル戻り値 MessageList・MessageInfo・Schedule共通
  const onClickGroupSubmit = () => {
    setUserCheckedIdList([]);
    setTokenState([]);
    setToken([]);
    setTopicState(groupCheckedIdList);
    selectToggle();
  };

  // 配信先利用者選択モーダル戻り値 MessageList・MessageInfo・Schedule共通
  const onClickUserSubmit = () => {
    setGroupCheckedIdList([]);
    setTopicState([]);
    setToken([]);
    setTokenState(userCheckedIdList);
    selectUserToggle();
  };

  // 配信先グループ選択モーダル開閉トリガー MessageList・MessageInfo・Schedule共通
  const selectToggle = () => {
    setIsGroupSelectOpen(!isGroupSelectOpen);
    const e = { target: { value: '' } }
    onSearch(e);
  };
  // 配信先利用者選択モーダル開閉トリガー MessageList・MessageInfo・Schedule共通
  const selectUserToggle = () => {
    setIsUserSelectOpen(!isUserSelectOpen);
    const e = { target: { value: '' } }
    onUserSearch(e);
  };

  // お知らせプレビュー MessageList・MessageInfo・Schedule共通
  const onClickPreview = async (data: Messages) => {
    await messageDatetimeSet(data, 'preview');
  };

  // お知らせ追加 MessageList・MessageInfo・Schedule共通
  const onClickSubmit = async () => {
    await messageDatetimeSet(previewData!, 'send');
  };

  // お知らせ下書き MessageList・MessageInfo・Schedule共通
  const onDraft = async (data: Messages) => {
    await messageDatetimeSet(data, 'draft');
  };

  // お知らせの日時関連をまとめてセット MessageList・MessageInfo・Scheduleほぼ共通
  const messageDatetimeSet = async (data: Messages, type: 'draft' | 'send' | 'preview'): Promise<void> => {
    if (!isDisabled.current) {
      let replyButtonFlg = false;
      try {
        isDisabled.current = true;
        if (data.eventFlg) {
          const result = createEvenReplyButtons().some(replyButton => replyButton.name === data.replyButton);
          if (!result) {
            replyButtonFlg = true;
            data.replyButton = "";
          }
        }
        const { sendDateList, sendData, sendToken } = await messageCommonFunctions.submit(
          data, type, onlineUrlSelected, currentUser,
          selectedEventType, base64Image, company,
          token, setToken, tokenState, topicState
        );

        // Firestore登録処理
        if (type === 'send' || type === 'draft') {
          // 配信または下書きの場合
          const result = await Promise.all(sendDateList.map(async (sendDateAndEventDateList) => {
            sendData.sendDate = sendDateAndEventDateList.sendDate;
            sendData.eventAt = sendDateAndEventDateList.eventAt ?? new Date();
            sendData.eventEndAt = sendDateAndEventDateList.eventEndAt ?? new Date();
            // 配信フラグ
            sendData.deliveredFlg = type === 'send' ? sendData.sendDate < new Date() : false;
            // tokenのdeliverdFlg更新
            sendToken.forEach((to) => {
              to.deliveredFlg = type === 'send' ? sendData.sendDate < new Date() : false;
              to.sendDate = sendData.sendDate;
            });
            return await messagesQuery.addMessage(sendData, sendToken, replyButtonFlg ? undefined : replyButtonSelected, questionnaireData); // 修正
          }));
          if (result.length === 0) {
            throw new Error();
          }
          const alertMessage = type === 'draft' ? "下書き保存しました。" : "お知らせを作成しました";
          dispatch(alertOpen(AlertValue(alertMessage, "success")));

          // addMessageListに追加または更新
          const newMessageId = result.at(0) ?? '';
          const publishState = sendData.draftFlg
            ? '下書き'
            : sendData.deliveredFlg
              ? '配信済み'
              : (sendData.publishEndAt !== undefined && new Date(sendData.publishEndAt) <= new Date())
                ? '掲載終了'
                : '未配信';
          const publishTarget = sendData.select === 'all' || sendData.select === company?.displayId!
            ? '全体'
            : sendData.select === 'topic' && topicState.length === 1
              ? 'G:' + (groupList?.find((group) => group.id === topicState.at(0))?.name ?? '')
              : sendData.select === 'topic' && topicState.length > 1
                ? '複数グループ'
                : sendData.select === 'token' && tokenState.length === 1
                  ? '個別:' + (userList?.find((user) => user.id === tokenState.at(0))?.lastName ?? '')
                  : sendData.select === 'token' && tokenState.length > 1
                    ? '複数利用者'
                    : '';
          const addMessageData: MessageView = {
            id: newMessageId,
            title: sendData.title,
            deliveredFlg: sendData.deliveredFlg,
            draftFlg: sendData.draftFlg,
            publishEndAt: sendData.publishEndAt ?? null,
            sendDate: sendData.sendDate,
            eventFlg: sendData.eventFlg,
            eventAt: sendData.eventAt ?? null,
            select: sendData.select ?? '',
            authorName: sendData.authorName ?? '',
            createAt: sendData.createAt,
            publishState: publishState,
            publishTarget: publishTarget,
            replyButtonId: !sendData.replyButton || sendData.replyButton === "" ? null : sendData.replyButton
          };
          setAddMessage(addMessageData);

          setTopicState([]);
          setTokenState([]);
          setCreateMessageFlg(!createMessageFlg);
          delayLoadFlg.current = true;
          toggle();
          isDisabled.current = false;
        } else {
          // プレビューの場合
          setPreviewData(sendData);
          togglePreview();
          isDisabled.current = false;
        }
        if (type === 'send') navigation("/message");
      } catch (err: any) {
        if (err === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue("ネットワークに接続できません。", "danger")));
        } else {
          console.error(err)
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        };
        isDisabled.current = false;
      };
    };
  };

  // 返信ボタンの変更をステートに保存 MessageList・MessageInfo・Schedule共通
  const onReplyButtonSelect = (id: string) => {
    setReplyButtonSelected(replyButtonList?.filter((item) => item.id === id)[0]);
  };

  // ミーティングURLの変更をステートに保存 MessageList・MessageInfo・Schedule共通
  const onOnlineUrlSelect = (url: string) => {
    setOnlineUrlSelected(url);
  };

  // イベントタイプの変更をステートに保存 MessageList・MessageInfo・Schedule共通
  const onEventTypeSelect = (eventType: string) => {
    setSelectedEventType(eventType);
  };

  // アンケートの変更をステートに保存 MessageList・MessageInfo・Schedule共通
  const onQuestionnaire = (data: QuestionnaireForm) => {
    setQuestionnaireData(data);
  };

  // 画像をbase64に変換して文字列としてステートに保存 MessageList・MessageInfo・Schedule共通
  const onSendImage = (file: File) => {
    try {
      if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_UNSUPPORT_IMG_FILE_TYPE, "danger")));
        return;
      };
      messageCommonFunctions.imageFileToBase64Image(file, setBase64Image);
    } catch (error: any) {
      console.error(error);
      dispatch(alertOpen(AlertValue("アップロードに失敗しました", "danger")));
    }
  };

  // お知らせ入力モーダルの開閉トリガー
  const toggle = async () => {
    setIsOpen(!isOpen);
    setOnlineUrlSelected('none');
    setReplyButtonSelected(undefined);
    setSelectedEventType('');
    setBase64Image('');

    if (userListLoadFlg.current === false) {
      userListLoadFlg.current = true;
      // 利用者を取得
      const getTargetUserData = await requestGetTargetUser(company?.id ?? '', [], true, false);
      if (getTargetUserData.success) {
        setUserList(getTargetUserData.users);
        const filteredUserList = getTargetUserData.users.filter((user) => user.lastName !== undefined && user.lastName !== '' && !user.deleteFlg && !user.accountSuspensionFlg);
        setFilteredUserList(filteredUserList);
        setInitFlg(true);
      }
    }
  };

  // プレビューモーダルの開閉トリガー MessageList・MessageInfo・Schedule共通
  const togglePreview = () => {
    setIsOpenPreview(!isOpenPreview);
  };

  const selectNumberList =
    [
      { id: 50, name: "50" },
      { id: 100, name: "100" },
      { id: 200, name: "200" },
    ];

  // お知らせ削除ダイアログ開閉
  const openDeleteDialog = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  //お知らせ削除
  const onClickDelete = async () => {
    setLoadProgress(0);
    openDeleteDialog();
    if(checkedList.length > 0){
      let notDeleteList: string[] = [];
      let deletedIds: string[] = [];
      for (let i = 0; i < checkedList.length; i++) {
        //チェックボックス選択したお知らせデータを取得
        const message = await messagesQuery.getMessage(checkedList[i]);
        //チェックリストに配信済み・掲載終了のお知らせがあるとき
        if(message.sendDate < new Date() && message.draftFlg === false){
          notDeleteList.push(message.title);
        }else{
          //削除実行
          await messagesQuery.deleteTopic(checkedList[i]);
          await messagesQuery.deleteMessage(checkedList[i]);
          deletedIds.push(checkedList[i]);
        }
        setLoadProgress(Math.round((i / checkedList.length) * 100));
      }

      setLoadProgress(100);

      // addMessageListから削除されたものを除外し、deleteMessageIdsに追加
      if (deletedIds.includes(addMessage?.id ?? '')) setAddMessage(null);
      setDeleteMessageIds([...deleteMessageIds, ...deletedIds]);

      setCheckedList([]);
      setCheckedAllValue(false);
      setDeleteCheckFlag(!deleteCheckFlag);
      delayLoadFlg.current = true;
      //チェックリストに配信済み・掲載終了のお知らせがあるとき
      if(notDeleteList.length > 0){
        dispatch(alertOpen(AlertValue(`選択したお知らせを削除しました。[${notDeleteList.join(',')}] は配信済みのため削除できませんでした。`, "danger")));
      }else{
        dispatch(alertOpen(AlertValue("選択したお知らせを削除しました。", "success")));
      }
    }
  };

  return (
    <div className="h-full">
      <Progress
        open={loadProgress >= 0 && loadProgress < 100}
        progressNum={loadProgress}
        color={"green"}
      />
      {isOpen && (
        <MessageEditModal
          open={isOpen}
          toggle={toggle}
          message={undefined}
          closeOnClickOutside={true}
          children={undefined}
          company={company}
          replyButtonSelected={replyButtonSelected}
          replyButtonList={replyButtonList}
          setReplyButtonList={setReplyButtonList}
          defaultReplyButtonList={defaultReplyButtonList}
          eventReplyButtonList={eventReplyButtonList}
          onlineUrlList={onlineUrlList}
          onClickPreview={onClickPreview}
          onDraft={onDraft}
          onReplyButtonSelect={onReplyButtonSelect}
          onQuestionnaire={onQuestionnaire}
          onOnlineUrlSelect={onOnlineUrlSelect}
          onEventTypeSelect={onEventTypeSelect}
          selectedEventType={selectedEventType}
          onSendImage={onSendImage}
          setBase64Image={setBase64Image}
          base64Image={base64Image}
          setTokenState={setTokenState}
          setTopicState={setTopicState}
          selectedColumns={selectedColumns}
          selectedRowList={selectedRowList}
          setIsGroupSelectOpen={setIsGroupSelectOpen}
          setIsUserSelectOpen={setIsUserSelectOpen}
          onlineUrlSelected={onlineUrlSelected}
        />)}
      {isGroupSelectOpen && (<GroupSelectModal
        open={isGroupSelectOpen}
        toggle={selectToggle}
        closeOnClickOutside={true}
        children={undefined}
        onClickGroupSubmit={onClickGroupSubmit}
        columns={groupFormColumns}
        rowList={groupFormRowList}
        onSearch={onSearch}
      />)}
      {isUserSelectOpen && (<UserSelectModal
        open={isUserSelectOpen}
        toggle={selectUserToggle}
        closeOnClickOutside={true}
        children={undefined}
        initFlg={initFlg}
        onClickUserSubmit={onClickUserSubmit}
        columns={userFormColumns}
        rowList={userFormRowList}
        onSearch={onUserSearch}
      />)}
      {isOpenPreview && (<MessagePreviewModal
        children={undefined}
        open={isOpenPreview}
        toggle={togglePreview}
        closeOnClickOutside={false}
        data={previewData}
        token={token}
        company={company!}
        groupList={groupList}
        userList={userList}
        replyButtonSelected={replyButtonSelected}
        onClickSubmit={onClickSubmit}
      />)}
      {/* 削除確認モーダル */}
      {isDialogOpen && (<MessageDeleteModal
        children={undefined}
        open={isDialogOpen}
        toggle={openDeleteDialog}
        closeOnClickOutside={false}
        handle={onClickDelete}
      //isLoad={isLoad}
      />)}
      <div className="flex sm:items-center px-6 pt-6 pb-4 justify-between">
        <div className="flex items-top sm:flex-auto space-x-4">
          <h2 className="min-w-[120px] text-xl font-semibold text-gray-900">
            お知らせ一覧
          </h2>
          <div>
            <h3 className="min-w-[380px] flex items-end">今月の投稿数：
              <h3 className="text-xl" style={{color:userCount*0.8 < messageCount? 'red':'black', fontWeight:userCount*0.8 < messageCount ?'bold':'normal'}}>{messageCount}</h3>
            </h3>
            <h3 className="text-sm text-gray-500">投稿数が上限{userCount}件の80％を超えた場合は赤字で表示されます</h3>
          </div>
        </div>
        {/* <div className={"flex flex-auto items-center space-x-2"}>
          {isLoad ? <Spiner /> : (<>
            <div>グループ選択</div>
            <SelectFields
              id="selectGroup"
              color="indigo"
              onChange={onGroupSelect}
              value={selectedGroupFilter}
              className={"h-10"}
              selectItems={groupFilterList}
            />
          </>)}
        </div> */}
        <div>
          <Button
            size={"sm"}
            onClick={toggle}
            children={"お知らせ新規作成"}
          />
        </div>
      </div>
      {isLoad ? <Spiner /> : (
        <>
          <div className={'px-6 pb-3'}>
            <MessageSearch
              groupList={groupList}
              replyButtonList={replyButtonList}
              company={company}
              checkedValue={checkedValue}
              setCheckedValue={setCheckedValue}
              checkedDate={checkedDate}
              setCheckedDate={setCheckedDate}
              searchMessageItem={searchMessageItem}
              setSearchMessageItem={setSearchMessageItem}
              onSearch={handleSearch}
            />
          </div>
          <div className={'px-6 pb-6'}>
            <TableList
              header
              gridCol={columns}
              gridRow={rowList}
              limit={selectNumber}
              selectNumberList={selectNumberList}
              setSelectNumber={setSelectNumber}
              openDeleteDialog={openDeleteDialog}
              checkedList={checkedList}
              setChangedCurrentPage={setChangedCurrentPage}
              gridRowLength={gridRowLength}
              firstPageFlg={firstPageFlg}
              setFirstPageFlg={setFirstPageFlg}
              pagingFunc={paging}
              minWidth="min-w-[900px]"
            />
          </div>
        </>
      )}
    </div>
  );
};
